import { selectBillingDetail, selectHasPaymentMethod } from "@app/billing";
import { FETCH_REQUIRED_DATA } from "@app/bootup";
import { createLog } from "@app/debug";
import { useDispatch, useSelector } from "@app/react";
import { setRedirectPath } from "@app/redirect-path";
import {
  homeUrl,
  loginUrl,
  logoutUrl,
  plansUrl,
  signupUrl,
  ssoRequiredUrl,
  verifyEmailRequestUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { selectAccessToken } from "@app/token";
import { selectCurrentUser, selectIsUserVerified } from "@app/users";
import { useLayoutEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSsoRequiredBlock } from "../hooks";
import { Loading } from "../shared";

const log = createLog("auth-required");
const denyList = [
  logoutUrl(),
  loginUrl(),
  signupUrl(),
  homeUrl(),
  verifyEmailRequestUrl(),
];

export const VerifyEmailRequired = ({
  children,
}: { children?: React.ReactNode }) => {
  const isUserVerified = useSelector(selectIsUserVerified);
  const user = useSelector(selectCurrentUser);

  if (user.id !== "" && !isUserVerified) {
    log("user not verified, redirecting to verify");
    return <Navigate to={verifyEmailRequestUrl()} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export const PaymentMethodRequired = ({
  children,
}: { children?: React.ReactNode }) => {
  const billingDetail = useSelector(selectBillingDetail);
  const hasPaymentMethod = useSelector(selectHasPaymentMethod);

  if (billingDetail.id !== "" && !hasPaymentMethod) {
    log("user has no payment method, redirecting to plans");
    return <Navigate to={plansUrl()} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

/*
 We need to accommodate the current redirects:

- User is not authenticated -> go to `/login`
- User is not verified -> go to `/verify`
- User has no payment method -> go to `/plans`
- User doesn't have an elevated token -> go to `/elevate`

This turned into quite an exploration and I had multiple existential crises.
I tried multiple different ways to streamline our forced redirects
and this current implementation was the one that won.

# How do forced redirects work?

We have a couple react components that will check for critical pieces
of data.  If that data meets the requirements for a forced redirect,
we render `<Navigate to={...} />`.

At the time of writing this we have 4 forced redirect components:

- `<AuthRequired />` -> checks for access token
- `<EmailVerifyRequired />` -> checks for user.verified
- `<PaymentMethodRequired />` -> checks for a payment method
- `<ElevateRequired />` -> checks for an elevated token in our redux store
*/
export const AuthRequired = ({
  children,
  redirectTo = loginUrl(),
}: { children?: React.ReactNode; redirectTo?: string }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const loader = useSelector((s) =>
    schema.loaders.selectById(s, { id: FETCH_REQUIRED_DATA }),
  );
  const accessToken = useSelector(selectAccessToken);

  // useLayoutEffect so redirect path is set before <Navigate> is processed.
  // Otherwise SignupPage can mount with an empty redirect path (e.g. invite flow).
  useLayoutEffect(() => {
    if (
      loader.status === "success" &&
      accessToken === "" &&
      !denyList.includes(location.pathname)
    ) {
      log(`setting redirect path ${location.pathname}`);
      dispatch(setRedirectPath(location.pathname));
    }
  }, [location.pathname, accessToken, loader.status, dispatch]);

  if (accessToken === "" && loader.lastSuccess > 0) {
    log("user not authenticated, redirecting to login");
    return <Navigate to={redirectTo} replace />;
  }

  if (accessToken === "") {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loading text="Loading token" />
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet />;
};

export const SsoRequired = ({ children }: { children?: React.ReactNode }) => {
  const { blocked } = useSsoRequiredBlock();

  if (blocked) {
    log("sso enforced but token is not sso-derived, redirecting");
    return <Navigate to={ssoRequiredUrl()} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export const AllRequired = ({ children }: { children?: React.ReactNode }) => {
  return (
    <AuthRequired>
      <VerifyEmailRequired>
        <PaymentMethodRequired>
          <SsoRequired>{children ? <>{children}</> : <Outlet />}</SsoRequired>
        </PaymentMethodRequired>
      </VerifyEmailRequired>
    </AuthRequired>
  );
};
